<template>
  <div>
    <!-- Mobile Header -->
    <header
      id="sitter-onboarding-nav-mobile"
      class="sitter-onboarding-nav-mobile fixed top-0 block lg:hidden w-full z-10 bg-foreground"
    >
      <nav>
        <div class="bg-foreground relative flex h-48 items-center">
          <button
            v-if="!mobileMenuOpen"
            class="pr-24 pl-16 accessible-border-mobile"
            aria-label="Open main navigation"
            @click="openMobileMenu"
          >
            <span class="sr-only">Open main navigation</span>
            <svg width="18" height="20" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <title>Open main navigation</title>
              <path
                d="M1 2C0.447715 2 0 1.55228 0 1C0 0.447715 0.447715 0 1 0H17C17.5523 0 18 0.447715 18 1C18 1.55228 17.5523 2 17 2H1Z"
                fill="#FF0064"
              />
              <path
                d="M1 8C0.447715 8 0 7.55228 0 7C0 6.44772 0.447715 6 1 6H17C17.5523 6 18 6.44772 18 7C18 7.55228 17.5523 8 17 8H1Z"
                fill="#FF0064"
              />
              <path
                d="M0 13C0 13.5523 0.447715 14 1 14H17C17.5523 14 18 13.5523 18 13C18 12.4477 17.5523 12 17 12H1C0.447715 12 0 12.4477 0 13Z"
                fill="#FF0064"
              />
            </svg>
          </button>

          <button v-else class="pr-24 pl-16 accessible-border-mobile" aria-label="Close main navigation" @click="closeMobileMenu">
            <span class="sr-only">Close main navigation</span>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <title>Close main navigation</title>
              <path
                d="M0.292893 15.7071C0.683418 16.0976 1.31658 16.0976 1.70711 15.7071L8 9.41421L14.2929 15.7071C14.6834 16.0976 15.3166 16.0976 15.7071 15.7071C16.0976 15.3166 16.0976 14.6834 15.7071 14.2929L9.41421 8L15.7071 1.70711C16.0976 1.31658 16.0976 0.683417 15.7071 0.292893C15.3166 -0.0976311 14.6834 -0.0976311 14.2929 0.292893L8 6.58579L1.70711 0.292893C1.31658 -0.0976311 0.683418 -0.0976311 0.292893 0.292893C-0.0976309 0.683417 -0.0976309 1.31658 0.292893 1.70711L6.58579 8L0.292893 14.2929C-0.0976311 14.6834 -0.0976311 15.3166 0.292893 15.7071Z"
                fill="#FF0064"
              />
            </svg>
          </button>

          <a href="/" class="accessible-border-mobile">
            <img src="@/images/components/header/sittercity-mark-logo.svg" alt="Sittercity" class="w-36" />
          </a>
        </div>

        <div
          v-if="mobileMenuOpen"
          id="sitter-onboarding-nav-panel-mobile"
          class="flex flex-col bg-foreground pt-48 h-screen w-full z-10"
        >
          <a
            href="https://www.sittercity.com/trust-and-safety"
            target="_blank"
            rel="noopener noreferrer"
            class="text-grey-text-subdued text-m1 link--tertiary pl-64 pt-40"
          >
            Trust & Safety
          </a>
          <a
            href="https://support.sittercity.com/hc/en-us"
            target="_blank"
            rel="noopener noreferrer"
            class="text-grey-text-subdued text-m1 link--tertiary mt-4 pl-64"
          >
            Help
          </a>
          <a href="/logout" class="text-grey-text-subdued text-m1 link--tertiary mt-4 pl-64"> Log out </a>
        </div>
      </nav>
    </header>

    <!-- Desktop Header -->
    <header
      id="sitter-onboarding-nav-desktop"
      class="fixed top-0 hidden lg:flex bg-foreground border-b-1 border-solid border-grey-divider-subdued w-full z-10"
    >
      <nav class="flex grow items-center">
        <a href="/" class="px-48 cursor-pointer" alt="Sittercity logo">
          <img src="@/images/components/header/sittercity-mark-logo.svg" alt="Sittercity" class="w-36" />
        </a>

        <div class="flex ml-auto pt-28 pb-32 mr-24 text-grey-clickable">
          <button
            id="sitter-onboarding-nav-dropdown-desktop-open"
            class="flex items-center accessible-border"
            :aria-expanded="desktopDropdownOpen ? 'true' : 'false'"
            aria-haspopup="true"
            @click="toggleDesktopDropdown"
          >
            {{ accountName }}
            <svg
              aria-hidden="true"
              class="ml-4"
              width="10"
              height="6"
              viewBox="0 0 10 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0.664906 0.5H9.33509C9.62634 0.5 9.82797 0.635602 9.93999 0.906805C10.052 1.17801 10.0072 1.41124 9.80557 1.60651L5.47048 5.80473C5.33605 5.93491 5.17923 6 5 6C4.82077 6 4.66395 5.93491 4.52952 5.80473L0.194431 1.60651C-0.00720122 1.41124 -0.0520082 1.17801 0.0600095 0.906805C0.172027 0.635602 0.37366 0.5 0.664906 0.5Z"
                fill="#4B4C52"
              />
            </svg>
          </button>

          <div
            v-if="desktopDropdownOpen"
            id="sitter-onboarding-nav-dropdown-desktop-panel"
            class="absolute bg-foreground p-12 right-6 shadow-lg top-14"
          >
            <button
              id="sitter-onboarding-nav-dropdown-desktop-close"
              class="flex items-center ml-auto mr-6 mb-16"
              @click="closeDesktopDropdown"
            >
              {{ accountName }}
              <svg
                class="ml-4 rotate-180 transform"
                aria-hidden="true"
                width="10"
                height="6"
                viewBox="0 0 10 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.664906 0.5H9.33509C9.62634 0.5 9.82797 0.635602 9.93999 0.906805C10.052 1.17801 10.0072 1.41124 9.80557 1.60651L5.47048 5.80473C5.33605 5.93491 5.17923 6 5 6C4.82077 6 4.66395 5.93491 4.52952 5.80473L0.194431 1.60651C-0.00720122 1.41124 -0.0520082 1.17801 0.0600095 0.906805C0.172027 0.635602 0.37366 0.5 0.664906 0.5Z"
                  fill="#4B4C52"
                />
              </svg>
            </button>

            <div class="flex flex-col pb-12">
              <a
                href="https://www.sittercity.com/trust-and-safety"
                target="_blank"
                rel="noopener noreferrer"
                class="text-body text-grey-clickable mb-12"
              >
                Trust & Safety
              </a>
              <a
                href="https://support.sittercity.com/hc/en-us"
                target="_blank"
                rel="noopener noreferrer"
                class="text-body text-grey-clickable mb-12"
              >
                Help
              </a>
              <a href="/logout" rel="nofollow" class="text-body text-grey-clickable"> Log out </a>
            </div>
          </div>
        </div>
      </nav>
    </header>
  </div>
</template>

<script lang="ts">
export default {
  name: 'SitterOnboardingNav',
  data() {
    return {
      mobileMenuOpen: false,
      desktopDropdownOpen: false,
    };
  },
  computed: {
    account() {
      return this.$store.getters['account/getAccount'];
    },
    accountName() {
      return this.account?.firstName || '';
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    this.closeMobileMenu();
  },
  methods: {
    openMobileMenu() {
      this.mobileMenuOpen = true;
      const body = document.querySelector('body');
      if (body) {
        body.classList.add('overflow-hidden');
      }
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false;
      const body = document.querySelector('body');
      if (body) {
        body.classList.remove('overflow-hidden');
      }
    },
    toggleDesktopDropdown() {
      this.desktopDropdownOpen = !this.desktopDropdownOpen;
    },
    closeDesktopDropdown() {
      this.desktopDropdownOpen = false;
    },
    handleClickOutside(event: Event) {
      const target = event.target as HTMLElement;
      const desktopPanel = document.getElementById('sitter-onboarding-nav-dropdown-desktop-panel');
      const desktopButton = document.getElementById('sitter-onboarding-nav-dropdown-desktop-open');

      if (
        this.desktopDropdownOpen &&
        desktopPanel &&
        desktopButton &&
        !desktopPanel.contains(target) &&
        !desktopButton.contains(target)
      ) {
        this.closeDesktopDropdown();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.sitter-onboarding-nav-mobile {
  box-shadow: 0 2px 12px rgba(215, 216, 219, 0.9);
}
</style>
